import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { ControlBridgeQueueItem } from '../deriveViewModel';
import {
  executeExoPanelAction,
  ControlBridgeExoPanelExecutionError,
} from '../../../services/controlBridgeExoPanelExecutionService';
import {
  fetchCoordinationNote,
  CoordinationNoteReadError,
} from '../../../services/controlBridgeCoordinationNoteService';
import {
  exoApprovalBreadcrumb,
  exoApprovalTrackedItemId,
  exoCardEyebrow,
  exoExecutionSuccessMessage,
  exoHeroActionLine,
  exoHeroContext,
  exoQueueRowSummary,
  exoSoftOperatorMessage,
  exoTrackedTransitionLine,
  exoUpNextItems,
  formatExoCalendarDayLabel,
  formatExoTodayLabel,
  exoMessageRoute,
} from './exoPanelHelpers';
import { resolveExoWorkDeeplink, type ExoWorkDeeplink } from './exoPanelWorkDeeplink';
import type { ExoPanelCalendarModel } from './exoPanelCalendarManifest';
import { deriveExoTransitionFlags } from '../derivation/exoQueueRanking';
import { findQueueItemById } from '../detail/findQueueItemById';
import { itemIdMatchesSource } from '../../../utils/itemIdPlane';
import {
  ExoUserResponseControls,
  emptyUserResponseDraft,
  buildApprovalChangePatches,
  buildPropertyPatchesFromDraft,
  exoChoiceIsFreeText,
  exoCardKind,
  exoCollectableSchemaFields,
  exoCompletionContract,
  isExoBinaryApprovalCard,
  isResponseSchemaDraftReady,
  normalizeResponseSchema,
  normalizeUserResponseOptions,
  normalizeUserResponseType,
  type ExoUserResponseDraft,
} from './ExoUserResponseControls';
import styles from '../ControlBridge.module.css';

function exoMessagesSummary(count: number): string {
  if (count <= 0) return 'No messages flagged for attention.';
  const noun = count === 1 ? 'item' : 'items';
  return `${count} ${noun} may need attention.`;
}

function readExoMachineryFlag(): boolean {
  try {
    return window.localStorage.getItem('krysalis.exoMachinery') === '1';
  } catch {
    return false;
  }
}

export interface ExoPanelPersonalLayerProps {
  queue: ControlBridgeQueueItem[];
  blockedCount: number;
  messageAttentionCount?: number;
  /** Open issues + recommended focus shown in the Messages expand panel. */
  messageItems?: ControlBridgeQueueItem[];
  calendar?: ExoPanelCalendarModel | null;
  /**
   * Calendar chip is held until personal calendar integration exists (Orlin
   * design ruling B6). Pass true only for explicit calendar previews/tests.
   */
  showCalendar?: boolean;
  scopeLabel?: string;
  resourceLabel?: string;
  onClearResource?: () => void;
  onOpenItemDetail?: (item: ControlBridgeQueueItem) => void;
  /**
   * Focus an item in the main Exo hero (inject into queue when absent from
   * eligible ranking). Used by Messages rows so the hero matches the slide-out.
   */
  onFocusExoItem?: (itemId: string) => void;
  /**
   * Messages / judgment clicks — Chat or Exo via resolveClickTarget.
   * Must not open the entity overlay (T3).
   */
  onRoutedItemClick?: (item: ControlBridgeQueueItem, invokingLabel?: string) => void;
  /** Blocked action routes to Chat with the item loaded (FE-EXO-4). */
  onBlockedRouteToChat?: (item: ControlBridgeQueueItem, reason: string) => void;
  /** Navigate to Drive / SPA route for “Go do work”. */
  onGoDoWork?: (item: ControlBridgeQueueItem, deeplink: ExoWorkDeeplink) => void;
  workspaceSlug?: string;
  onManifestRefresh?: () => void | Promise<void>;
  /** Sidebar / external focus request — select this item when present in the queue. */
  focusedItemId?: string | null;
  /** Called after focusedItemId is applied (or abandoned) so the parent can clear the request id. */
  onFocusedItemApplied?: () => void;
  /**
   * Clear parent-held inject pin (ExoPanelSurface). Called when the operator picks a
   * different row or focus cannot be satisfied — not on successful apply, so injection
   * survives clearing ``focusedItemId``.
   */
  onClearFocusPin?: () => void;
  /**
   * Krysalis/admin: show ids, SM names, priority, ages, depends-on refs.
   * When omitted, `localStorage.krysalis.exoMachinery === '1'` enables it.
   */
  showMachinery?: boolean;
}

type HeroMode = 'act' | 'blocking' | 'deferring' | 'changing';

export const ExoPanelPersonalLayer: React.FC<ExoPanelPersonalLayerProps> = ({
  queue,
  blockedCount,
  messageAttentionCount: _messageAttentionCount = 0,
  messageItems = [],
  calendar,
  showCalendar = false,
  scopeLabel,
  resourceLabel,
  onClearResource,
  onOpenItemDetail,
  onFocusExoItem,
  onRoutedItemClick,
  onBlockedRouteToChat,
  onGoDoWork,
  workspaceSlug,
  onManifestRefresh,
  focusedItemId = null,
  onFocusedItemApplied,
  onClearFocusPin,
  showMachinery: showMachineryProp,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(() => queue[0]?.itemId ?? null);
  const [queueExpanded, setQueueExpanded] = useState(false);
  const [calendarExpanded, setCalendarExpanded] = useState(false);
  const [messagesExpanded, setMessagesExpanded] = useState(false);
  const [heroMode, setHeroMode] = useState<HeroMode>('act');
  const [blockReason, setBlockReason] = useState('');
  const [deferReason, setDeferReason] = useState('');
  const [changeChoice, setChangeChoice] = useState('');
  const [changeFreeText, setChangeFreeText] = useState('');
  const [executionPending, setExecutionPending] = useState(false);
  const [executionError, setExecutionError] = useState<string | null>(null);
  const [executionSuccess, setExecutionSuccess] = useState<string | null>(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [noteBody, setNoteBody] = useState<string | null>(null);
  const [noteUnavailable, setNoteUnavailable] = useState(false);
  const [noteMessage, setNoteMessage] = useState<string | null>(null);
  const [responseDraft, setResponseDraft] = useState<ExoUserResponseDraft>(() => emptyUserResponseDraft());
  const [responseReady, setResponseReady] = useState(true);
  /** Focus id already applied (or consumed by operator pick) — avoid re-forcing selection. */
  const appliedFocusRef = useRef<string | null>(null);
  const showMachinery = showMachineryProp ?? readExoMachineryFlag();

  useEffect(() => {
    if (focusedItemId) {
      if (findQueueItemById(queue, focusedItemId)) {
        if (appliedFocusRef.current !== focusedItemId) {
          setSelectedId(focusedItemId);
          appliedFocusRef.current = focusedItemId;
        }
        // Clear the shell request id only. Keep Surface inject pin until the operator
        // picks another row — otherwise Me/lens-only rows drop out of queue immediately.
        onFocusedItemApplied?.();
        return;
      }
      // Focus request cannot be satisfied — clear request + inject pin.
      appliedFocusRef.current = null;
      onFocusedItemApplied?.();
      onClearFocusPin?.();
    } else {
      appliedFocusRef.current = null;
    }
    if (queue.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !findQueueItemById(queue, selectedId)) {
      setSelectedId(queue[0]!.itemId);
    }
  }, [queue, selectedId, focusedItemId, onFocusedItemApplied, onClearFocusPin]);

  useEffect(() => {
    setHeroMode('act');
    setBlockReason('');
    setDeferReason('');
    setChangeChoice('');
    setChangeFreeText('');
    setExecutionError(null);
    setExecutionSuccess(null);
    setNoteOpen(false);
    setNoteLoading(false);
    setNoteError(null);
    setNoteBody(null);
    setNoteUnavailable(false);
    setNoteMessage(null);
    const draft = emptyUserResponseDraft();
    setResponseDraft(draft);
    const focusItem =
      (selectedId ? findQueueItemById(queue, selectedId) : undefined) ?? queue[0] ?? null;
    if (!focusItem) {
      setResponseReady(true);
      return;
    }
    if (focusItem.responseSchemaBlockedReason) {
      setResponseReady(false);
      return;
    }
    const contract = exoCompletionContract(focusItem);
    if (!contract.actionable) {
      setResponseReady(false);
      return;
    }
    const fields = exoCollectableSchemaFields(normalizeResponseSchema(focusItem.responseSchema));
    setResponseReady(isResponseSchemaDraftReady(fields, draft));
    // Intentionally selectedId-only: remanifest must not wipe an in-progress response draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- queue snapshot at selection change
  }, [selectedId]);

  const runExecution = async (
    action: 'complete' | 'defer' | 'block',
    item: ControlBridgeQueueItem,
    options?: {
      blockReason?: string;
      deferReason?: string;
      propertyPatches?: ReturnType<typeof buildPropertyPatchesFromDraft>;
      trackedItemId?: string;
    }
  ) => {
    const executionSlug = (item.workspaceSlug ?? workspaceSlug ?? '').trim();
    if (!executionSlug) {
      setExecutionError(exoSoftOperatorMessage('Workspace slug is required to execute transitions.'));
      setExecutionSuccess(null);
      return;
    }
    if (action === 'complete') {
      const contract = exoCompletionContract(item);
      if (!contract.actionable) {
        setExecutionError(
          exoSoftOperatorMessage(contract.missing ?? 'This item is not ready to save yet.')
        );
        setExecutionSuccess(null);
        return;
      }
    }
    const schemaFields = exoCollectableSchemaFields(normalizeResponseSchema(item.responseSchema));
    let propertyPatches: ReturnType<typeof buildPropertyPatchesFromDraft> | undefined;
    let trackedItemId: string | undefined;
    if (action === 'complete' && options?.propertyPatches?.length) {
      propertyPatches = options.propertyPatches;
      trackedItemId = options.trackedItemId?.trim() || exoApprovalTrackedItemId(item);
      if (!trackedItemId) {
        setExecutionError('There is no record to write this answer on.');
        setExecutionSuccess(null);
        return;
      }
    } else if (action === 'complete' && schemaFields.length > 0) {
      if (!isResponseSchemaDraftReady(schemaFields, responseDraft)) {
        setExecutionError('Answer all required fields first.');
        setExecutionSuccess(null);
        return;
      }
      trackedItemId = item.responseSchemaTargetRef?.trim();
      if (!trackedItemId) {
        setExecutionError('There is no record to write this answer on.');
        setExecutionSuccess(null);
        return;
      }
      propertyPatches = buildPropertyPatchesFromDraft(schemaFields, responseDraft);
      if (!propertyPatches.length) {
        setExecutionError('Answer all required fields first.');
        setExecutionSuccess(null);
        return;
      }
    }
    setExecutionPending(true);
    setExecutionError(null);
    setExecutionSuccess(null);
    try {
      const executionResult = await executeExoPanelAction(executionSlug, {
        itemId: item.itemId,
        action,
        targetState: item.activityTargetState,
        blockReason: options?.blockReason,
        deferReason: options?.deferReason,
        trackedItemId,
        propertyPatches,
      });
      if (executionResult.patchReceipt?.autoFireNextError) {
        setExecutionError(
          `Patch committed, but the configured automatic hop failed: ${executionResult.patchReceipt.autoFireNextError}`
        );
        setExecutionSuccess(null);
      } else {
        setExecutionSuccess(
          exoExecutionSuccessMessage(action, executionResult.toState, {
            showMachinery,
            approvalCard: isExoBinaryApprovalCard(item),
          })
        );
      }
      setHeroMode('act');
      setBlockReason('');
      setDeferReason('');
      setChangeChoice('');
      setChangeFreeText('');
      await onManifestRefresh?.();
    } catch (err) {
      const raw =
        err instanceof ControlBridgeExoPanelExecutionError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'That action did not finish.';
      setExecutionError(showMachinery ? raw : exoSoftOperatorMessage(raw));
      setExecutionSuccess(null);
      // Non-patch responses may still have applied a disk write; refresh so the UI
      // matches actual state before the operator retries (same operation id reuse).
      if (
        err instanceof ControlBridgeExoPanelExecutionError &&
        err.code === 'PERSIST_MODE_NOT_PATCH'
      ) {
        try {
          await onManifestRefresh?.();
        } catch {
          // Keep the persistMode error primary; refresh failure is secondary.
        }
      }
    } finally {
      setExecutionPending(false);
    }
  };

  const selectedIndex = useMemo(
    () => queue.findIndex((item) => itemIdMatchesSource(selectedId, item)),
    [queue, selectedId]
  );
  // Prefer selectedId; if focus/filter races left selection unset, still show queue[0]
  // so a non-empty queue never collapses to "All caught up".
  const selected =
    (selectedIndex >= 0 ? queue[selectedIndex] : undefined) ?? (queue.length > 0 ? queue[0]! : null);
  const resolvedSelectedIndex =
    selectedIndex >= 0 ? selectedIndex : selected && queue.length > 0 ? 0 : -1;
  const heroContext = selected ? exoHeroContext(selected, { showMachinery }) : null;
  const cardKind = selected ? exoCardKind(selected) : null;
  const trackedTransitionLine = selected ? exoTrackedTransitionLine(selected) : undefined;
  const completionContract = selected ? exoCompletionContract(selected) : null;
  const transitionFlags = selected ? deriveExoTransitionFlags(selected) : null;
  const workDeeplink = selected ? resolveExoWorkDeeplink(selected) : null;
  const upNext = exoUpNextItems(queue, selected?.itemId ?? selectedId);
  const upNextCount = upNext.length;

  const openCoordinationNote = async (item: ControlBridgeQueueItem) => {
    const ref = item.coordinationNoteRef?.trim();
    if (!ref) return;
    setNoteOpen(true);
    setNoteError(null);
    setNoteBody(null);
    setNoteUnavailable(false);
    setNoteMessage(null);
    const slug = (item.workspaceSlug ?? workspaceSlug ?? '').trim();
    if (!slug) {
      setNoteUnavailable(true);
      setNoteMessage('Workspace slug is required to load the coordination note body.');
      return;
    }
    setNoteLoading(true);
    try {
      const result = await fetchCoordinationNote(slug, ref);
      setNoteBody(result.bodyMarkdown ?? null);
      setNoteUnavailable(Boolean(result.unavailable) || !result.bodyMarkdown);
      setNoteMessage(result.message ?? null);
    } catch (err) {
      const message =
        err instanceof CoordinationNoteReadError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to load coordination note';
      setNoteError(message);
    } finally {
      setNoteLoading(false);
    }
  };

  const selectItem = (itemId: string) => {
    if (focusedItemId) {
      appliedFocusRef.current = focusedItemId;
    }
    setSelectedId(itemId);
    setQueueExpanded(false);
    setHeroMode('act');
    setBlockReason('');
    setDeferReason('');
    setChangeChoice('');
    setChangeFreeText('');
    setExecutionError(null);
    setExecutionSuccess(null);
    onFocusedItemApplied?.();
    onClearFocusPin?.();
  };

  // Count and list must share one source (Orlin B5). Never fall back to a
  // rollup/attention number when messageItems is empty — that showed "10" with
  // an empty Messages panel.
  const messagesCount = messageItems.length;

  const todayChipLabel = calendar?.todayLabel
    ? formatExoCalendarDayLabel(calendar.todayLabel)
    : formatExoTodayLabel();

  const contextBar = (
    <ExoContextBar
      scopeLabel={scopeLabel}
      resourceLabel={resourceLabel}
      onClearResource={onClearResource}
      showCalendar={showCalendar}
      calendarExpanded={calendarExpanded}
      calendarChipLabel={todayChipLabel}
      messagesExpanded={messagesExpanded}
      messageAttentionCount={messagesCount}
      queueExpanded={queueExpanded}
      upNextCount={upNextCount}
      onToggleCalendar={() => setCalendarExpanded((v) => !v)}
      onToggleMessages={() => setMessagesExpanded((v) => !v)}
      onToggleQueue={() => setQueueExpanded((v) => !v)}
    />
  );

  const expandPanels = (
    <>
      {showCalendar && calendarExpanded ? (
        <ExoCalendarPanel
          calendar={calendar}
          todayLabel={calendar?.todayLabel}
          showMachinery={showMachinery}
        />
      ) : null}
      {messagesExpanded ? (
        <div className={styles.exoStripExpandPanel} data-testid="exo-messages-panel">
          <p className={styles.exoStripExpandMeta}>{exoMessagesSummary(messagesCount)}</p>
          {messageItems.length > 0 ? (
            <ul className={styles.exoMessagesList} data-testid="exo-messages-list">
              {messageItems.map((item) => (
                <li key={item.itemId} className={styles.exoMessagesRow}>
                  <button
                    type="button"
                    className={styles.exoMessagesRowButton}
                    onClick={() => {
                      const route = exoMessageRoute(item);
                      if (route.kind === 'exo') {
                        if (onFocusExoItem) {
                          onFocusExoItem(item.itemId);
                        } else if (findQueueItemById(queue, item.itemId)) {
                          selectItem(item.itemId);
                        }
                      } else {
                        onRoutedItemClick?.(item, 'Exo · Messages');
                      }
                      setMessagesExpanded(false);
                    }}
                  >
                    <span className={styles.exoMessagesRowTitle}>{item.title}</span>
                    {showMachinery && item.priority ? (
                      <span className={styles.exoMessagesRowMeta}>{item.priority}</span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {queueExpanded && upNextCount > 0 ? (
        <div className={styles.exoQueuePickPanel} role="list" aria-label="Up next queue">
          {upNext.map((item) => (
            <button
              key={item.itemId}
              type="button"
              role="listitem"
              className={styles.exoQueueRow}
              aria-label={exoQueueRowSummary(item)}
              onClick={() => selectItem(item.itemId)}
            >
              <span className={styles.exoQueueRowTitle}>{item.title}</span>
              {exoHeroActionLine(item) ? (
                <span className={styles.exoQueueRowActionMuted}>{exoHeroActionLine(item)}</span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </>
  );

  if (queue.length === 0 || !selected || !heroContext) {
    return (
      <div className={styles.exoPanelLayout} data-testid="exo-panel-personal-layer">
        {contextBar}
        {expandPanels}
        <div className={styles.exoEmptyState} role="status">
          <div className={styles.exoEmptyTitle}>All caught up</div>
          <p className={styles.exoEmptyBody}>
            No eligible items in this queue.
            {blockedCount > 0
              ? ` ${blockedCount} activit${blockedCount === 1 ? 'y is' : 'ies are'} blocked or waiting on prerequisites.`
              : ''}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.exoPanelLayout} data-testid="exo-panel-personal-layer">
      {contextBar}
      {expandPanels}

      <section
        className={styles.exoHeroCard}
        aria-label="Selected work item"
        data-exo-card-kind={cardKind ?? undefined}
        data-testid="exo-hero-card"
      >
        <div className={styles.exoHeroTopRow}>
          {isExoBinaryApprovalCard(selected) ? (
            <div className={styles.exoHeroBreadcrumb} data-testid="exo-hero-breadcrumb">
              {exoApprovalBreadcrumb(
                selected,
                resolvedSelectedIndex,
                queue.length,
                scopeLabel
              )}
            </div>
          ) : (
            <div className={styles.exoHeroEyebrow} data-testid="exo-hero-eyebrow">
              {exoCardEyebrow(cardKind ?? 'instance')}
            </div>
          )}
          {onOpenItemDetail ? (
            <button
              type="button"
              className={styles.exoHeroDetailsLink}
              onClick={() => onOpenItemDetail(selected)}
              data-testid="exo-hero-details"
            >
              Details ↗
            </button>
          ) : null}
        </div>
        {isExoBinaryApprovalCard(selected) ? (
          <>
            <h2 className={styles.exoHeroTitle} data-testid="exo-hero-prompt">
              {selected.userResponsePrompt}
            </h2>
            <p className={styles.exoHeroActivityTitle} data-testid="exo-hero-item-title">
              {selected.title}
            </p>
          </>
        ) : (
          <>
            {/* Activity title stays in the hero. Tracked-record name is context chrome, not a redirect. */}
            <h2 className={styles.exoHeroTitle} data-testid="exo-hero-item-title">
              {selected.title}
            </h2>
            {exoHeroActionLine(selected) ? (
              <>
                <div className={styles.exoHeroEyebrow}>Action to be taken</div>
                <p className={styles.exoHeroAction}>{exoHeroActionLine(selected)}</p>
              </>
            ) : null}
            {trackedTransitionLine ? (
              <p className={styles.exoHeroAction} data-testid="exo-hero-tracked-transition">
                {trackedTransitionLine}
              </p>
            ) : null}
          </>
        )}

        <div className={styles.exoContextBox} data-testid="exo-hero-context">
          <div className={styles.exoContextLabel}>Context</div>
          {heroContext.metaLine ? (
            <p className={styles.exoContextBody}>{heroContext.metaLine}</p>
          ) : null}
          {heroContext.recordContext ? (
            <dl className={styles.exoRecordContext} data-testid="exo-hero-record-context">
              {heroContext.recordContext.name &&
              heroContext.recordContext.name.trim() !== selected.title.trim() ? (
                <div className={styles.exoRecordContextRow}>
                  <dt>Tracked record</dt>
                  <dd>{heroContext.recordContext.name}</dd>
                </div>
              ) : null}
              {heroContext.recordContext.customer ? (
                <div className={styles.exoRecordContextRow}>
                  <dt>Customer</dt>
                  <dd>{heroContext.recordContext.customer}</dd>
                </div>
              ) : null}
              {heroContext.recordContext.value ? (
                <div className={styles.exoRecordContextRow}>
                  <dt>Value</dt>
                  <dd>{heroContext.recordContext.value}</dd>
                </div>
              ) : null}
              {heroContext.recordContext.ageAtStep ? (
                <div className={styles.exoRecordContextRow}>
                  <dt>Age at step</dt>
                  <dd>{heroContext.recordContext.ageAtStep}</dd>
                </div>
              ) : null}
              {heroContext.recordContext.lastTouch ? (
                <div className={styles.exoRecordContextRow}>
                  <dt>Last touch</dt>
                  <dd>{heroContext.recordContext.lastTouch}</dd>
                </div>
              ) : null}
              {heroContext.recordContext.owner &&
              heroContext.recordContext.owner !== heroContext.assignee ? (
                <div className={styles.exoRecordContextRow}>
                  <dt>Owner</dt>
                  <dd>{heroContext.recordContext.owner}</dd>
                </div>
              ) : null}
            </dl>
          ) : null}
          {heroContext.evidence ? (
            <p className={styles.exoContextDescription} data-testid="exo-hero-evidence">
              <span className={styles.exoContextMetaKey}>Why we think so</span>
              {heroContext.evidence}
            </p>
          ) : null}
          {heroContext.actorKindLabel ? (
            <p className={styles.exoContextMetaLine} data-testid="exo-hero-actor">
              <span className={styles.exoContextMetaKey}>{heroContext.actorKindLabel}</span>
              {heroContext.actorDisplay ?? ''}
            </p>
          ) : heroContext.assignee ? (
            <p className={styles.exoContextMetaLine} data-testid="exo-hero-assignee">
              <span className={styles.exoContextMetaKey}>Assignee</span>
              {heroContext.assignee}
            </p>
          ) : null}
          {heroContext.effort ? (
            <p className={styles.exoContextMetaLine} data-testid="exo-hero-effort">
              <span className={styles.exoContextMetaKey}>Effort</span>
              {heroContext.effort}
            </p>
          ) : null}
          {heroContext.waypointLabel ? (
            <p className={styles.exoContextMetaLine} data-testid="exo-hero-waypoint">
              <span className={styles.exoContextMetaKey}>Waypoint</span>
              {heroContext.waypointLabel}
            </p>
          ) : null}
          {showMachinery && heroContext.description ? (
            <p className={styles.exoContextDescription} data-testid="exo-hero-description">
              {heroContext.description}
            </p>
          ) : null}
          {heroContext.issueSummary ? (
            <p className={styles.exoContextDescription} data-testid="exo-hero-issue-summary">
              {heroContext.issueSummary}
            </p>
          ) : null}
          {heroContext.acceptanceHints.length > 0 ? (
            <div className={styles.exoContextSection} data-testid="exo-hero-acceptance">
              <div className={styles.exoContextSectionLabel}>Acceptance</div>
              <ul className={styles.exoContextList}>
                {heroContext.acceptanceHints.map((hint) => (
                  <li key={hint}>{hint}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {heroContext.blockedReason ? (
            <p className={styles.exoContextBlocked} data-testid="exo-hero-blocked-reason" role="status">
              <span className={styles.exoContextMetaKey}>Blocked</span>
              {heroContext.blockedReason}
            </p>
          ) : null}
          {heroContext.deferReason ? (
            <p className={styles.exoContextBlocked} data-testid="exo-hero-defer-reason" role="status">
              <span className={styles.exoContextMetaKey}>Deferred</span>
              {heroContext.deferReason}
            </p>
          ) : null}
          {heroContext.dependsOnRefs.length > 0 ? (
            <div className={styles.exoContextSection} data-testid="exo-hero-depends-on">
              <div className={styles.exoContextSectionLabel}>Depends on</div>
              <ul className={styles.exoContextList}>
                {heroContext.dependsOnRefs.map((ref) => (
                  <li key={ref}>
                    <code className={styles.exoContextRef}>{ref}</code>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {selected.coordinationNoteRef ? (
            <div className={styles.exoContextSection} data-testid="exo-hero-coord-note">
              <button
                type="button"
                className={styles.exoNoteOpenButton}
                onClick={() => void openCoordinationNote(selected)}
                aria-expanded={noteOpen}
              >
                {noteOpen ? 'Hide coordination note' : 'Open coordination note'}
              </button>
              {noteOpen ? (
                <div className={styles.exoNotePanel} data-testid="exo-note-panel">
                  {showMachinery ? (
                    <code className={styles.exoContextRef}>{selected.coordinationNoteRef}</code>
                  ) : null}
                  {noteLoading ? <p className={styles.exoNoteMeta}>Loading note…</p> : null}
                  {noteError ? (
                    <p className={styles.exoExecutionError} role="alert">
                      {noteError}
                    </p>
                  ) : null}
                  {noteUnavailable && !noteLoading ? (
                    <p className={styles.exoNoteMeta} data-testid="exo-note-unavailable">
                      {noteMessage ||
                        'Note body API not deployed yet. Path shown for discovery; respond waits on G3.'}
                    </p>
                  ) : null}
                  {noteBody ? (
                    <pre className={styles.exoNoteBody} data-testid="exo-note-body">
                      {noteBody}
                    </pre>
                  ) : null}
                  <button
                    type="button"
                    className={styles.exoNoteRespond}
                    disabled
                    title="Respond requires coordination-note HTTP API (G3)"
                    data-testid="exo-note-respond"
                  >
                    Respond (API pending)
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {transitionFlags?.requiresBridgeSession ? (
          <div className={styles.exoBridgeDivert} data-testid="exo-bridge-session-divert" role="status">
            <p className={styles.exoContextBody}>
              This transition needs a full Bridge Session. Open Chat to continue with judgment and
              model change — Exo will not execute it here.
            </p>
            {onOpenItemDetail ? (
              <button
                type="button"
                className={styles.exoWorkCta}
                onClick={() => onOpenItemDetail(selected)}
              >
                Open in Chat →
              </button>
            ) : null}
          </div>
        ) : null}

        {workDeeplink && onGoDoWork && !transitionFlags?.requiresBridgeSession ? (
          <div className={styles.exoWorkCtaRow}>
            <button
              type="button"
              className={styles.exoWorkCta}
              data-testid="exo-go-do-work"
              onClick={() => onGoDoWork(selected, workDeeplink)}
            >
              {workDeeplink.label}
            </button>
          </div>
        ) : null}

        {heroMode === 'act' && !transitionFlags?.requiresBridgeSession ? (
          <>
            {completionContract?.missing ? (
              <p
                className={styles.exoContextBlocked}
                data-testid="exo-contract-incomplete"
                role="status"
              >
                {exoSoftOperatorMessage(completionContract.missing)}
              </p>
            ) : null}
            <ExoUserResponseControls
              item={selected}
              draft={responseDraft}
              onDraftChange={setResponseDraft}
              onValidityChange={setResponseReady}
            />
            <div className={styles.exoActions} role="group" aria-label="Execution actions">
              {executionError ? (
                <p
                  className={styles.exoExecutionError}
                  role="alert"
                  data-testid="exo-execution-error"
                >
                  {executionError}
                </p>
              ) : null}
              {executionSuccess ? (
                <p
                  className={styles.exoExecutionSuccess}
                  role="status"
                  data-testid="exo-execution-success"
                >
                  {executionSuccess}
                </p>
              ) : null}
              <button
                type="button"
                className={styles.exoActionGood}
                disabled={
                  executionPending ||
                  !workspaceSlug ||
                  !responseReady ||
                  Boolean(completionContract && !completionContract.actionable)
                }
                onClick={() => void runExecution('complete', selected)}
              >
                {executionPending
                  ? 'Working…'
                  : isExoBinaryApprovalCard(selected)
                    ? 'Approve'
                    : normalizeUserResponseType(selected.userResponseType) === 'signoff'
                      ? 'Acknowledge'
                      : 'Complete'}
              </button>
              {isExoBinaryApprovalCard(selected) ? (
                <button
                  type="button"
                  className={styles.exoActionWarn}
                  disabled={executionPending || !workspaceSlug}
                  onClick={() => {
                    setExecutionSuccess(null);
                    setExecutionError(null);
                    setChangeChoice('');
                    setChangeFreeText('');
                    setHeroMode('changing');
                  }}
                >
                  Change
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className={styles.exoActionWarn}
                    disabled={executionPending || !workspaceSlug}
                    onClick={() => {
                      setExecutionSuccess(null);
                      setExecutionError(null);
                      setHeroMode('deferring');
                    }}
                  >
                    Do later
                  </button>
                  <button
                    type="button"
                    className={styles.exoActionAlert}
                    disabled={executionPending}
                    onClick={() => {
                      setExecutionSuccess(null);
                      setExecutionError(null);
                      setHeroMode('blocking');
                    }}
                  >
                    Blocked
                  </button>
                </>
              )}
            </div>
            {isExoBinaryApprovalCard(selected) ? (
              <div className={styles.exoQuietLinks} data-testid="exo-approval-quiet-links">
                <button
                  type="button"
                  className={styles.exoQuietLink}
                  disabled={executionPending || !workspaceSlug}
                  onClick={() => {
                    setExecutionSuccess(null);
                    setExecutionError(null);
                    setHeroMode('deferring');
                  }}
                >
                  Later
                </button>
                <button
                  type="button"
                  className={styles.exoQuietLink}
                  disabled={executionPending}
                  onClick={() => {
                    setExecutionSuccess(null);
                    setExecutionError(null);
                    setHeroMode('blocking');
                  }}
                >
                  Not sure — ask someone else
                </button>
              </div>
            ) : null}
          </>
        ) : heroMode === 'changing' && !transitionFlags?.requiresBridgeSession ? (
          <div className={styles.exoChangePanel} data-testid="exo-change-panel">
            <div className={styles.exoChangeTitle}>Change</div>
            {normalizeUserResponseOptions(selected.userResponseOptions).length === 0 ? (
              <p className={styles.exoContextBlocked} role="status">
                No change options are defined for this item yet.
              </p>
            ) : (
              <div
                className={styles.exoChoiceList}
                role="radiogroup"
                aria-label="Change options"
              >
                {normalizeUserResponseOptions(selected.userResponseOptions).map((opt) => (
                  <label key={opt.value} className={styles.exoChoiceRow}>
                    <input
                      type="radio"
                      name={`exo-change-${selected.itemId}`}
                      value={opt.value}
                      checked={changeChoice === opt.value}
                      onChange={() => setChangeChoice(opt.value)}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            )}
            {normalizeUserResponseOptions(selected.userResponseOptions).some(
              (opt) => opt.value === changeChoice && exoChoiceIsFreeText(opt)
            ) ? (
              <input
                type="text"
                className={styles.exoDeferInput}
                aria-label="Someone else"
                placeholder="Who instead?"
                value={changeFreeText}
                onChange={(e) => setChangeFreeText(e.target.value)}
                data-testid="exo-change-free-text"
              />
            ) : null}
            {executionError ? (
              <p
                className={styles.exoExecutionError}
                role="alert"
                data-testid="exo-execution-error"
              >
                {executionError}
              </p>
            ) : null}
            <div className={styles.exoBlockActions}>
              <button
                type="button"
                className={styles.exoBlockCancel}
                disabled={executionPending}
                onClick={() => setHeroMode('act')}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.exoActionGood}
                disabled={
                  executionPending ||
                  !workspaceSlug ||
                  !changeChoice ||
                  (normalizeUserResponseOptions(selected.userResponseOptions).some(
                    (opt) => opt.value === changeChoice && exoChoiceIsFreeText(opt)
                  ) &&
                    !changeFreeText.trim())
                }
                onClick={() => {
                  const options = normalizeUserResponseOptions(selected.userResponseOptions);
                  const chosen = options.find((opt) => opt.value === changeChoice);
                  const writeValue =
                    chosen && exoChoiceIsFreeText(chosen)
                      ? changeFreeText.trim()
                      : changeChoice.trim();
                  const patches = buildApprovalChangePatches(selected, writeValue);
                  if (!patches.length) {
                    setExecutionError(
                      'There is no record field to save this change on.'
                    );
                    return;
                  }
                  void runExecution('complete', selected, {
                    propertyPatches: patches,
                    trackedItemId: exoApprovalTrackedItemId(selected),
                  });
                }}
              >
                {executionPending ? 'Working…' : 'Save'}
              </button>
            </div>
          </div>
        ) : heroMode === 'deferring' && !transitionFlags?.requiresBridgeSession ? (
          <div className={styles.exoBlockPanel} data-testid="exo-defer-panel">
            <div className={styles.exoBlockTitle}>Why defer this?</div>
            <textarea
              className={styles.exoBlockInput}
              rows={2}
              value={deferReason}
              placeholder="Optional — leave blank to use the system defer note."
              aria-label="Defer reason"
              onChange={(e) => setDeferReason(e.target.value)}
            />
            {executionError ? (
              <p
                className={styles.exoExecutionError}
                role="alert"
                data-testid="exo-execution-error"
              >
                {executionError}
              </p>
            ) : null}
            <div className={styles.exoBlockActions}>
              <button
                type="button"
                className={styles.exoBlockCancel}
                disabled={executionPending}
                onClick={() => setHeroMode('act')}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.exoActionWarn}
                disabled={executionPending || !workspaceSlug}
                onClick={() => {
                  void runExecution('defer', selected, {
                    deferReason: deferReason.trim() || undefined,
                  });
                }}
              >
                {executionPending ? 'Working…' : 'Confirm defer'}
              </button>
            </div>
          </div>
        ) : heroMode === 'blocking' && !transitionFlags?.requiresBridgeSession ? (
          <div className={styles.exoBlockPanel}>
            <div className={styles.exoBlockTitle}>What&apos;s blocking this?</div>
            <textarea
              className={styles.exoBlockInput}
              rows={2}
              value={blockReason}
              placeholder="Describe the blocker — Chat opens with this item loaded."
              aria-label="Blocker reason"
              onChange={(e) => setBlockReason(e.target.value)}
            />
            {executionError ? (
              <p
                className={styles.exoExecutionError}
                role="alert"
                data-testid="exo-execution-error"
              >
                {executionError}
              </p>
            ) : null}
            <div className={styles.exoBlockActions}>
              <button
                type="button"
                className={styles.exoBlockCancel}
                disabled={executionPending}
                onClick={() => setHeroMode('act')}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.exoActionAlert}
                disabled={!blockReason.trim() || executionPending}
                onClick={() => {
                  const reason = blockReason.trim();
                  if (onBlockedRouteToChat) {
                    onBlockedRouteToChat(selected, reason);
                    setHeroMode('act');
                    setBlockReason('');
                    return;
                  }
                  void runExecution('block', selected, { blockReason: reason });
                }}
              >
                {onBlockedRouteToChat ? 'Open Chat with blocker' : executionPending ? 'Logging…' : 'Log blocker'}
              </button>
            </div>
          </div>
        ) : null}
      </section>

      {blockedCount > 0 ? (
        <p className={styles.exoBlockedFootnote}>
          {blockedCount} more activit{blockedCount === 1 ? 'y is' : 'ies are'} model-ready but blocked — they will
          surface in the queue when prerequisites and resources are satisfied.
        </p>
      ) : null}
    </div>
  );
};

function ExoCalendarPanel({
  calendar,
  todayLabel,
  showMachinery = false,
}: {
  calendar?: ExoPanelCalendarModel | null;
  todayLabel?: string;
  showMachinery?: boolean;
}) {
  const events = calendar?.events ?? [];
  const focusDate = todayLabel ?? events[0]?.scheduledDate;

  return (
    <div className={styles.exoStripExpandPanel} data-testid="exo-calendar-panel">
      {events.length === 0 ? (
        <p className={styles.exoStripExpandMeta}>No scheduled items in manifest for this window.</p>
      ) : (
        <>
          <p className={styles.exoStripExpandMeta}>
            {focusDate ? `Schedule for ${formatExoCalendarDayLabel(focusDate)}` : 'Today\u2019s schedule'}
            {' · '}
            {events.length} item{events.length === 1 ? '' : 's'}
          </p>
          <ul className={styles.exoCalendarList} aria-label="Calendar schedule">
            {events.map((event) => {
              const meta = showMachinery
                ? [event.statusPhrase, event.priority].filter(Boolean).join(' · ')
                : '';
              return (
              <li key={event.itemId} className={styles.exoCalendarRow} data-testid={`exo-calendar-row-${event.itemId}`}>
                <span className={styles.exoCalendarDate}>{event.scheduledDate}</span>
                <span className={styles.exoCalendarTitle}>{event.title}</span>
                {meta ? <span className={styles.exoCalendarMeta}>{meta}</span> : null}
              </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

function ExoContextBar({
  scopeLabel,
  resourceLabel,
  onClearResource,
  showCalendar = false,
  calendarExpanded,
  calendarChipLabel,
  messagesExpanded,
  messageAttentionCount,
  queueExpanded,
  upNextCount,
  onToggleCalendar,
  onToggleMessages,
  onToggleQueue,
}: {
  scopeLabel?: string;
  resourceLabel?: string;
  onClearResource?: () => void;
  showCalendar?: boolean;
  calendarExpanded: boolean;
  calendarChipLabel: string;
  messagesExpanded: boolean;
  messageAttentionCount: number;
  queueExpanded: boolean;
  upNextCount: number;
  onToggleCalendar: () => void;
  onToggleMessages: () => void;
  onToggleQueue: () => void;
}) {
  return (
    <div className={styles.exoContextBar}>
      {scopeLabel && resourceLabel ? (
        <div className={styles.exoContextMeta} aria-label="Active filters">
          <span>
            Viewing: <strong>{scopeLabel}</strong>
          </span>
          <span aria-hidden="true"> · </span>
          <span>
            Resource: <strong>{resourceLabel}</strong>
            {onClearResource && resourceLabel !== 'All resources' ? (
              <button type="button" className={styles.clearFilter} onClick={onClearResource}>
                Clear
              </button>
            ) : null}
          </span>
        </div>
      ) : null}
      <div className={styles.exoContextTools}>
        {showCalendar ? (
          <button
            type="button"
            className={`${styles.exoMinimizedChip} ${calendarExpanded ? styles.exoMinimizedChipOn : ''}`}
            aria-expanded={calendarExpanded}
            aria-label="Calendar"
            onClick={onToggleCalendar}
          >
            Calendar · {calendarChipLabel}
          </button>
        ) : null}
        <button
          type="button"
          className={`${styles.exoMinimizedChip} ${messagesExpanded ? styles.exoMinimizedChipOn : ''}`}
          aria-expanded={messagesExpanded}
          aria-label="Messages"
          onClick={onToggleMessages}
        >
          Messages{messageAttentionCount > 0 ? ` · ${messageAttentionCount}` : ''}
        </button>
        {upNextCount > 0 ? (
          <button
            type="button"
            className={`${styles.exoMinimizedChip} ${queueExpanded ? styles.exoMinimizedChipOn : ''}`}
            aria-expanded={queueExpanded}
            aria-label={`Up next · ${upNextCount}`}
            onClick={onToggleQueue}
          >
            Up next · {upNextCount}
          </button>
        ) : null}
      </div>
    </div>
  );
}
